/**
 * Skeleton for the All Stories search results — shaped to match the real
 * card list, not generic gray rectangles.
 *
 * Pattern (Algolia + Material guidance): the skeleton must mirror the row
 * shape that will replace it. Anything else causes a visible reflow when
 * the real rows arrive ("pinball effect" — NN/g).
 *
 * Two skeleton kinds:
 *   - Story skeleton: image block left + title bar + summary bar + meta
 *     row, mirroring `StoryGridCard`.
 *   - Brief skeleton: single thin row, mirroring `CompactBriefRow`.
 *
 * They're interleaved roughly in the proportion of a real result list
 * (briefs are more common). Shimmer animation is `explore-skeleton-bone`
 * already defined in `global.css` (1.6s ease-in-out infinite).
 */

const ROWS: Array<"story" | "brief"> = [
  "brief",
  "story",
  "brief",
  "brief",
  "story",
  "brief",
  "brief",
];

export function SearchResultsSkeleton() {
  return (
    <output aria-busy="true" className="flex flex-col gap-3">
      <span className="sr-only">Loading search results…</span>
      {ROWS.map((kind, idx) =>
        kind === "story" ? (
          <StorySkeleton key={`${kind}-${idx}`} />
        ) : (
          <BriefSkeleton key={`${kind}-${idx}`} />
        )
      )}
    </output>
  );
}

function StorySkeleton() {
  return (
    <div className="flex gap-4 rounded-2xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)] p-4">
      {/* Image block — desktop only; on narrow screens the real card
          stacks the image, so we hide the block to keep heights aligned. */}
      <div className="explore-skeleton-bone hidden h-[110px] w-[180px] shrink-0 sm:block" />
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-2.5">
        {/* Title — two bars (title can wrap to 2 lines) */}
        <div className="explore-skeleton-bone h-4 w-[80%]" />
        <div className="explore-skeleton-bone h-4 w-[55%]" />
        {/* Summary — single thin bar */}
        <div className="explore-skeleton-bone h-3 w-[90%] opacity-70" />
        {/* Meta row — small chip + date dot */}
        <div className="mt-1 flex items-center gap-2">
          <div className="explore-skeleton-bone h-4 w-16 rounded-md" />
          <div className="explore-skeleton-bone h-3 w-12 rounded-md opacity-70" />
        </div>
      </div>
    </div>
  );
}

function BriefSkeleton() {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)] px-4 py-3">
      <div className="explore-skeleton-bone h-3.5 w-[55%]" />
      <div className="ml-auto hidden items-center gap-1.5 sm:flex">
        <div className="explore-skeleton-bone h-4 w-14 rounded-full" />
        <div className="explore-skeleton-bone h-4 w-12 rounded-full opacity-70" />
      </div>
    </div>
  );
}
